/**
 * Merging two versions of a learner profile.
 *
 * The hard case in syncing progress is not the network — it is that the same
 * student can generate work in two places before either reaches the server. A
 * child practises on a tablet in the car with no signal, then opens a laptop at
 * home. Both devices hold real work, and *neither* is authoritative.
 *
 * Last-write-wins would silently throw one afternoon of effort away, so this
 * merges field by field under one rule: **never lose evidence of learning.**
 * Where two values conflict, the one representing more work survives.
 *
 * Pure, and takes `now` like everything else in this package.
 */

import { RECENT_WINDOW, type ConceptState } from './mastery.ts';
import { MAX_STREAK_FREEZES } from './progress.ts';
import { ATTEMPT_LOG_LIMIT, type AttemptRecord, type DailyProgress, type LearnerProfile, type RpgState } from './types.ts';

/**
 * Merges two profiles for the same learner.
 *
 * `local` and `remote` are interchangeable — the result is the same whichever
 * way round they are passed, which matters because a device cannot know whether
 * it is "ahead".
 */
export function mergeProfiles(local: LearnerProfile, remote: LearnerProfile): LearnerProfile {
  // Identity comes from whichever was created first: that is the original
  // account, and a later device's profile is the copy.
  const primary = local.createdAt <= remote.createdAt ? local : remote;
  const other = primary === local ? remote : local;

  return {
    id: primary.id,
    // The most recently *edited* display name wins, approximated by whoever
    // has been active more recently. Names change rarely; guessing wrong is cheap.
    displayName: lastActiveAt(local) >= lastActiveAt(remote) ? local.displayName : remote.displayName,
    ageBand: primary.ageBand === 'unknown' ? other.ageBand : primary.ageBand,
    ...(pickLevel(local, remote) ? { levelId: pickLevel(local, remote) } : {}),
    createdAt: Math.min(local.createdAt, remote.createdAt),

    // XP is a running total on each device, not a delta, so the larger figure
    // already includes the shared history. Summing would double-count every
    // merge and inflate without limit.
    xp: Math.max(local.xp, remote.xp),
    streak: mergeStreaks(local, remote),

    concepts: mergeConcepts(local.concepts, remote.concepts),
    completedLessonIds: [...new Set([...local.completedLessonIds, ...remote.completedLessonIds])],
    // An earned badge is never revoked, so the earliest unlock time wins.
    achievements: mergeAchievements(local, remote),

    attempts: mergeAttempts(local.attempts, remote.attempts),

    stats: {
      totalAttempts: Math.max(local.stats.totalAttempts, remote.stats.totalAttempts),
      totalCorrect: Math.max(local.stats.totalCorrect, remote.stats.totalCorrect),
      totalStudyMs: Math.max(local.stats.totalStudyMs, remote.stats.totalStudyMs),
      lessonsCompleted: Math.max(local.stats.lessonsCompleted, remote.stats.lessonsCompleted),
      questsCompleted: Math.max(local.stats.questsCompleted ?? 0, remote.stats.questsCompleted ?? 0),
      bestCombo: Math.max(local.stats.bestCombo ?? 0, remote.stats.bestCombo ?? 0),
    },

    ...mergeDaily(local.daily, remote.daily),
    ...(pickWeeklyGoal(local, remote) !== undefined ? { weeklyGoalDays: pickWeeklyGoal(local, remote) } : {}),

    ...pickDiagnostic(local, remote),
    ...(primary.goalConceptId || other.goalConceptId
      ? { goalConceptId: primary.goalConceptId ?? other.goalConceptId }
      : {}),

    ...mergeRpg(local, remote),
  };
}

const lastActiveAt = (profile: LearnerProfile): number =>
  profile.attempts[profile.attempts.length - 1]?.at ?? profile.createdAt;

function pickLevel(a: LearnerProfile, b: LearnerProfile): LearnerProfile['levelId'] | undefined {
  return lastActiveAt(a) >= lastActiveAt(b) ? (a.levelId ?? b.levelId) : (b.levelId ?? a.levelId);
}

/**
 * Streaks.
 *
 * `current` cannot simply be maxed: two devices used on the same day are one
 * day of practice, not two. But a device that practised yesterday *and* today
 * genuinely has the longer run, so the larger current streak is the honest one
 * — and `totalActiveDays` is maxed for the same reason XP is.
 */
function mergeStreaks(a: LearnerProfile, b: LearnerProfile): LearnerProfile['streak'] {
  const later = (a.streak.lastActiveDay ?? '') >= (b.streak.lastActiveDay ?? '') ? a.streak : b.streak;
  return {
    current: Math.max(a.streak.current, b.streak.current),
    longest: Math.max(a.streak.longest, b.streak.longest),
    lastActiveDay: later.lastActiveDay,
    totalActiveDays: Math.max(a.streak.totalActiveDays, b.streak.totalActiveDays),
    // Rest days are the exception to "more work wins": the *smaller* balance is
    // the honest one, because a device showing more simply has not seen the
    // spend yet. Handing back a rest day that has already been used would let
    // two devices cover a fortnight between them.
    freezes: Math.min(MAX_STREAK_FREEZES, Math.min(a.streak.freezes ?? 0, b.streak.freezes ?? 0)),
    freezeUsedOn: later.freezeUsedOn ?? a.streak.freezeUsedOn ?? b.streak.freezeUsedOn ?? null,
  };
}

/**
 * Today's counters.
 *
 * Only mergeable when both devices are talking about the same local day —
 * otherwise the later day simply stands, because yesterday's counters mean
 * nothing to today's quests. Within a day the larger count wins, and claimed
 * quests are unioned so a reward paid on one device is not paid again on the
 * other.
 */
function mergeDaily(
  a: DailyProgress | undefined,
  b: DailyProgress | undefined,
): Pick<LearnerProfile, 'daily'> | Record<string, never> {
  if (!a && !b) return {};
  if (!a) return { daily: b as DailyProgress };
  if (!b) return { daily: a };
  if (a.day !== b.day) return { daily: a.day > b.day ? a : b };

  return {
    daily: {
      day: a.day,
      attempts: Math.max(a.attempts, b.attempts),
      correct: Math.max(a.correct, b.correct),
      hardCorrect: Math.max(a.hardCorrect, b.hardCorrect),
      unaidedCorrect: Math.max(a.unaidedCorrect, b.unaidedCorrect),
      reviewAttempts: Math.max(a.reviewAttempts, b.reviewAttempts),
      lessonsCompleted: Math.max(a.lessonsCompleted, b.lessonsCompleted),
      // The live run belongs to whichever device is further into it; a run
      // interrupted by picking up another device is not really broken.
      combo: Math.max(a.combo, b.combo),
      bestCombo: Math.max(a.bestCombo, b.bestCombo),
      conceptIds: [...new Set([...a.conceptIds, ...b.conceptIds])],
      questIds: a.questIds.length >= b.questIds.length ? a.questIds : b.questIds,
      claimedQuestIds: [...new Set([...a.claimedQuestIds, ...b.claimedQuestIds])],
    },
  };
}

function pickWeeklyGoal(a: LearnerProfile, b: LearnerProfile): number | undefined {
  return lastActiveAt(a) >= lastActiveAt(b)
    ? (a.weeklyGoalDays ?? b.weeklyGoalDays)
    : (b.weeklyGoalDays ?? a.weeklyGoalDays);
}

function mergeAchievements(a: LearnerProfile, b: LearnerProfile): LearnerProfile['achievements'] {
  const earliest = new Map<string, number>();
  for (const entry of [...a.achievements, ...b.achievements]) {
    const known = earliest.get(entry.id);
    if (known === undefined || entry.unlockedAt < known) earliest.set(entry.id, entry.unlockedAt);
  }
  return [...earliest.entries()]
    .map(([id, unlockedAt]) => ({ id, unlockedAt }))
    .sort((x, y) => x.unlockedAt - y.unlockedAt);
}

function mergeConcepts(
  a: Readonly<Record<string, ConceptState>>,
  b: Readonly<Record<string, ConceptState>>,
): Record<string, ConceptState> {
  const out: Record<string, ConceptState> = {};
  for (const id of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const left = a[id];
    const right = b[id];
    if (!left) {
      out[id] = right as ConceptState;
    } else if (!right) {
      out[id] = left;
    } else {
      out[id] = mergeConceptState(left, right);
    }
  }
  return out;
}

/**
 * Merging one concept's state.
 *
 * Counts overlap — both devices share history up to the point they diverged —
 * so summing would inflate attempts. Maxing each counter keeps the record
 * consistent with the device that did the most work, which is the closest
 * honest reconstruction available without a full attempt-level replay.
 */
export function mergeConceptState(a: ConceptState, b: ConceptState): ConceptState {
  // Recency-weighted accuracy reads `recent`, so it must come from *one*
  // device rather than being interleaved: whichever practised last is current.
  const fresher = (a.lastPracticedAt ?? 0) >= (b.lastPracticedAt ?? 0) ? a : b;
  const staler = fresher === a ? b : a;

  const misconceptions: Record<string, number> = { ...staler.misconceptions };
  for (const [id, count] of Object.entries(fresher.misconceptions)) {
    misconceptions[id] = Math.max(misconceptions[id] ?? 0, count);
  }

  return {
    conceptId: a.conceptId,
    attempts: Math.max(a.attempts, b.attempts),
    correct: Math.max(a.correct, b.correct),
    recent: fresher.recent.slice(-RECENT_WINDOW),
    hardAttempts: Math.max(a.hardAttempts, b.hardAttempts),
    hardCorrect: Math.max(a.hardCorrect, b.hardCorrect),
    ceiling: Math.max(a.ceiling, b.ceiling),
    lessonCompleted: a.lessonCompleted || b.lessonCompleted,
    ...pickTime('firstStudiedAt', Math.min, a, b),
    ...pickTime('lastPracticedAt', Math.max, a, b),

    // Review scheduling belongs to whichever device practised most recently:
    // its interval and due date reflect the actual last recall. Taking the
    // longer interval would push the review out past the point of forgetting.
    intervalDays: fresher.intervalDays,
    ease: fresher.ease,
    ...(fresher.dueAt !== undefined ? { dueAt: fresher.dueAt } : {}),
    reviews: Math.max(a.reviews, b.reviews),

    // A placement estimate survives only while *both* devices still have one.
    // If either has seen the student practise, the estimate has been answered
    // and the merged state is a real history, not a guess.
    ...(a.placed && b.placed ? { placed: true } : {}),

    misconceptions,
  };
}

function pickTime(
  key: 'firstStudiedAt' | 'lastPracticedAt',
  choose: (x: number, y: number) => number,
  a: ConceptState,
  b: ConceptState,
): Partial<ConceptState> {
  const left = a[key];
  const right = b[key];
  if (left === undefined && right === undefined) return {};
  if (left === undefined) return { [key]: right };
  if (right === undefined) return { [key]: left };
  return { [key]: choose(left, right) };
}

/**
 * Attempts are an append-only log, so a true union is possible. Two records are
 * the same attempt when they share a timestamp and an exercise — a student
 * cannot answer the same problem twice in the same millisecond.
 */
function mergeAttempts(a: readonly AttemptRecord[], b: readonly AttemptRecord[]): AttemptRecord[] {
  const seen = new Map<string, AttemptRecord>();
  for (const attempt of [...a, ...b]) {
    seen.set(`${attempt.at}:${attempt.exerciseId}`, attempt);
  }
  return [...seen.values()].sort((x, y) => x.at - y.at).slice(-ATTEMPT_LOG_LIMIT);
}

/**
 * The RPG slice.
 *
 * A slice missing on one side means that device predates the adventure or has
 * not synced since it began — the other side wins wholesale, for the same
 * reason a concept studied on only one device is kept.
 */
function mergeRpg(a: LearnerProfile, b: LearnerProfile): Pick<LearnerProfile, 'rpg'> | Record<string, never> {
  const left = a.rpg;
  const right = b.rpg;
  if (!left && !right) return {};
  if (!left) return { rpg: right };
  if (!right) return { rpg: left };

  // The side whose character was edited most recently holds the student's
  // current look; a side with no character at all never outranks one with one.
  const newerEdit = (left.character?.updatedAt ?? 0) >= (right.character?.updatedAt ?? 0) ? left : right;

  return {
    rpg: {
      // Character: the newer edit wins — cosmetics are a choice, not work, and the latest choice is the current one.
      ...(newerEdit.character ? { character: newerEdit.character } : {}),
      // Gold is only ever earned by claims, and claims union below, so the larger
      // balance is the more settled one. Never summed — a device pair would
      // double-pay every reward on every merge.
      gold: Math.max(left.gold, right.gold),
      // A quest accepted anywhere is accepted, and the earlier acceptedAt is when the story actually began.
      questLog: mergeQuestLog(left.questLog, right.questLog),
      // A payment seen anywhere happened; the union is what keeps claiming idempotent across devices.
      claimedQuestIds: unionOf(left.claimedQuestIds, right.claimedQuestIds),
      // A beaten boss stays beaten, whichever device saw the fight.
      clearedBossIds: unionOf(left.clearedBossIds, right.clearedBossIds),
      // A fight in progress: the later attempt, and within an attempt the
      // further-through side. See `mergeBattles`.
      ...mergeBattles(left.battles, right.battles),
      // A journal entry read on either device has been read; the union is the
      // only rule that cannot re-mark an old mistake as news on the other one.
      ...mergeJournalSeen(left.journalSeen, right.journalSeen),
      // A mistake named on either device has been named. Union, like every
      // other set here — and losing one would cost nothing but a tick in the
      // bestiary, because naming a mistake is not a claim about mathematics.
      ...mergeNamedTricks(left.namedTrickIds, right.namedTrickIds),
      // Anything built on either device is on the map. Union by tile, keeping
      // the earlier build where both devices put something on the same square —
      // the same rule `questLog` uses for an acceptance, and for the same
      // reason: it is the only tiebreak that gives one answer whichever side
      // the merge is run from.
      ...mergeBuilds(left.builds, right.builds),
      // A cosmetic owned anywhere is owned — dropping one would lose the gold already spent on it.
      inventory: unionOf(left.inventory, right.inventory),
      // Worn cosmetics travel with the newer character edit, so the outfit matches the avatar it was dressed on.
      equipped: { ...newerEdit.equipped },
    },
  };
}

const unionOf = (a: readonly string[], b: readonly string[]): string[] => [...new Set([...a, ...b])];

/**
 * Journal entries read, unioned — but the field is only written when there is
 * something in it, so a merge of two profiles that never opened the journal
 * does not invent an empty list neither of them had.
 */
function mergeJournalSeen(
  a: RpgState['journalSeen'],
  b: RpgState['journalSeen'],
): Pick<RpgState, 'journalSeen'> | Record<string, never> {
  const seen = unionOf(a ?? [], b ?? []);
  return seen.length > 0 ? { journalSeen: seen } : {};
}

/**
 * Mistakes named, unioned — and, like `journalSeen`, only written when there is
 * something in it, so merging two profiles that never sealed a ghost does not
 * invent an empty list neither of them had.
 */
function mergeNamedTricks(
  a: RpgState['namedTrickIds'],
  b: RpgState['namedTrickIds'],
): Pick<RpgState, 'namedTrickIds'> | Record<string, never> {
  const named = unionOf(a ?? [], b ?? []);
  return named.length > 0 ? { namedTrickIds: named } : {};
}

/**
 * Boss battles.
 *
 * Two rules, and both of them are "keep the fight that got further":
 *
 * - **A higher `attemptIndex` wins wholesale.** A retry is a different fight
 *   with different questions, so its answers cannot be merged with the
 *   abandoned one's — mixing them would score answers against questions
 *   nobody was asked. The newer attempt is the one the student is in.
 * - **Within one attempt, the longer list wins.** The list only ever grows,
 *   one answer at a time, so the longer one is the same fight seen later.
 *   Never concatenated: two devices each two questions in are two views of
 *   one fight, not a four-question fight.
 *
 * Losing a merge costs a student nothing they cannot redo — the fight is
 * replayable and free — which is why the simple rule is the right one here.
 */
function mergeBattles(
  a: RpgState['battles'],
  b: RpgState['battles'],
): Pick<RpgState, 'battles'> | Record<string, never> {
  if (!a && !b) return {};
  const out: NonNullable<RpgState['battles']> = { ...a };
  for (const [bossId, incoming] of Object.entries(b ?? {})) {
    const known = out[bossId];
    if (!known) {
      out[bossId] = incoming;
      continue;
    }
    if (incoming.attemptIndex !== known.attemptIndex) {
      out[bossId] = incoming.attemptIndex > known.attemptIndex ? incoming : known;
      continue;
    }
    out[bossId] = incoming.answers.length > known.answers.length ? incoming : known;
  }
  return { battles: out };
}

function mergeQuestLog(a: RpgState['questLog'], b: RpgState['questLog']): RpgState['questLog'] {
  const out: RpgState['questLog'] = { ...a };
  for (const [id, entry] of Object.entries(b)) {
    const known = out[id];
    out[id] = known && known.acceptedAt <= entry.acceptedAt ? known : entry;
  }
  return out;
}

/** The more recent diagnostic wins: it is a better estimate of where they are. */
function pickDiagnostic(a: LearnerProfile, b: LearnerProfile): Pick<LearnerProfile, 'diagnostic'> | Record<string, never> {
  const left = a.diagnostic;
  const right = b.diagnostic;
  if (!left && !right) return {};
  if (!left) return { diagnostic: right };
  if (!right) return { diagnostic: left };
  return { diagnostic: left.completedAt >= right.completedAt ? left : right };
}

/**
 * Whether two profiles differ in any way worth writing to the server.
 *
 * Used to avoid a network round trip on every keystroke-level state change.
 */
export function profilesDiffer(a: LearnerProfile, b: LearnerProfile): boolean {
  return (
    a.xp !== b.xp ||
    a.displayName !== b.displayName ||
    a.stats.totalAttempts !== b.stats.totalAttempts ||
    a.stats.lessonsCompleted !== b.stats.lessonsCompleted ||
    a.completedLessonIds.length !== b.completedLessonIds.length ||
    a.achievements.length !== b.achievements.length ||
    a.streak.current !== b.streak.current ||
    // A student who only moved their weekly target has still made a change
    // worth carrying to their other device, and it touches nothing else here.
    a.weeklyGoalDays !== b.weeklyGoalDays ||
    Object.keys(a.concepts).length !== Object.keys(b.concepts).length ||
    lastActiveAt(a) !== lastActiveAt(b) ||
    // A character edit, an accepted quest or a spent coin touches none of the
    // fields above, but is still progress the student's other device should see.
    rpgDiffers(a.rpg, b.rpg)
  );
}

function rpgDiffers(a: RpgState | undefined, b: RpgState | undefined): boolean {
  if (!a || !b) return Boolean(a) !== Boolean(b);
  return (
    a.gold !== b.gold ||
    a.claimedQuestIds.length !== b.claimedQuestIds.length ||
    a.clearedBossIds.length !== b.clearedBossIds.length ||
    a.inventory.length !== b.inventory.length ||
    // Reading the journal on one device should not leave the other one
    // flagging the same old mistake as new.
    (a.journalSeen?.length ?? 0) !== (b.journalSeen?.length ?? 0) ||
    // A ghost named on one device should show as named on the other.
    (a.namedTrickIds?.length ?? 0) !== (b.namedTrickIds?.length ?? 0) ||
    (a.builds?.length ?? 0) !== (b.builds?.length ?? 0) ||
    Object.keys(a.questLog).length !== Object.keys(b.questLog).length ||
    // A fight in progress is worth carrying: a student who answered two
    // questions on a phone should not meet question one again on a laptop.
    battleSignature(a.battles) !== battleSignature(b.battles) ||
    Object.keys(a.equipped).length !== Object.keys(b.equipped).length ||
    (a.character?.updatedAt ?? 0) !== (b.character?.updatedAt ?? 0)
  );
}

/** Attempt and length per boss, order-independent — the two things the merge reads. */
/**
 * Builds from both devices, one to a tile.
 *
 * Absent on both sides means absent in the result, like `battles` and
 * `journalSeen`: a profile that never built anything must not grow an empty
 * array it did not have, or two profiles that are the same would stop
 * comparing equal.
 */
function mergeBuilds(
  left: RpgState['builds'],
  right: RpgState['builds'],
): Pick<RpgState, 'builds'> {
  if (!left && !right) return {};
  const byTile = new Map<string, NonNullable<RpgState['builds']>[number]>();
  for (const build of [...(left ?? []), ...(right ?? [])]) {
    const key = `${build.x},${build.y}`;
    const seen = byTile.get(key);
    if (!seen || build.at < seen.at) byTile.set(key, build);
  }
  // Sorted, so the merged list is the same list whichever side was `left`.
  const builds = [...byTile.values()].sort((a, b) => a.at - b.at || a.x - b.x || a.y - b.y);
  return { builds };
}

function battleSignature(battles: RpgState['battles']): string {
  return Object.entries(battles ?? {})
    .map(([bossId, record]) => `${bossId}:${record.attemptIndex}:${record.answers.length}`)
    .sort()
    .join('|');
}
